"use client";

import { useEffect } from "react";

import { ErrorState } from "@/components/ds/States";

// Erro de carregamento de chunk = um deploy novo trocou os arquivos JS enquanto
// a aba estava aberta; o chunk antigo que a página pediu não existe mais. Não é
// bug — recarregar pega os arquivos novos.
const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

// Mesma causa (deploy com a aba aberta), outro sintoma: uma referência de
// client-component fica obsoleta e o React tenta renderizar um objeto no lugar
// do componente → "Minified React error #306" (Element type is invalid: got
// object). Em produção a mensagem vem minificada. Tratar como recuperável: o
// reload pega o manifest novo. Se persistir após 1 reload (bug real de import),
// o guard de sessionStorage segura e a tela de erro normal aparece.
const STALE_DEPLOY_RENDER_RE =
  /Minified React error #306|Element type is invalid/i;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
    // Auto-recupera de erro de deploy-com-aba-aberta: full reload 1x (carrega os
    // chunks/manifest novos). Cobre ChunkLoadError E o #306 (client-ref obsoleta
    // virando objeto). Guard em sessionStorage evita loop se não resolver.
    const msg = error?.message ?? "";
    const isStaleDeployError =
      CHUNK_ERROR_RE.test(msg) ||
      CHUNK_ERROR_RE.test(error?.name ?? "") ||
      STALE_DEPLOY_RENDER_RE.test(msg);
    if (isStaleDeployError && typeof window !== "undefined") {
      const KEY = "obra:chunk-reload-at";
      let last = 0;
      try {
        last = Number(window.sessionStorage.getItem(KEY) ?? "0");
      } catch {
        /* incognito/quota — segue sem guard */
      }
      if (Date.now() - last > 10_000) {
        try {
          window.sessionStorage.setItem(KEY, String(Date.now()));
        } catch {
          /* ignora */
        }
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-ground px-4">
      <ErrorState
        title="Algo deu errado"
        text={
          <>
            Encontramos um erro ao processar sua solicitação. Tente novamente.
            {error.digest && (
              <span className="mt-2 block font-mono text-2xs text-ink-2">
                ID: {error.digest}
              </span>
            )}
          </>
        }
        onRetry={reset}
        className="w-full max-w-md"
      />
    </div>
  );
}
